import * as THREE from 'three';
import { BaseObject, applyProperties } from './base';

function updateSymmetryVector3(vector3, properties) {
  if (!vector3) {
    vector3 = new THREE.Vector3(0, 0, 0);
  }
  vector3 = new THREE.Vector3(vector3.x, vector3.y, vector3.z);
  const plane = properties.symmetryPlane || 'yz';
  const offset = properties.symmetryOffset || 1;

  const matrix = new THREE.Matrix4();
  const scale = new THREE.Vector3(1, 1, 1);
  const translation = new THREE.Vector3(0, 0, 0);

  switch (plane) {
    case 'xy': // Mirror across X-Y plane (flip Z)
      scale.z = -1;
      translation.z = offset * 2;
      break;
    case 'xz': // Mirror across X-Z plane (flip Y)
      scale.y = -1;
      translation.y = offset * 2;
      break;
    case 'yz': // Mirror across Y-Z plane (flip X)
      scale.x = -1;
      translation.x = offset * 2;
      break;
  }

  matrix.makeScale(scale.x, scale.y, scale.z);
  const translationMatrix = new THREE.Matrix4().makeTranslation(translation.x, translation.y, translation.z);
  matrix.premultiply(translationMatrix);

  const vector3out = vector3.applyMatrix4(matrix);
  return { x: vector3out.x, y: vector3out.y, z: vector3out.z };
}

function updateSymmetryRotation(rotation, properties) {
  const plane = properties.symmetryPlane || 'yz';

  // Convert rotation to radians if needed (assuming input is in degrees)
  const rotationRad = {
    x: (rotation.x * Math.PI) / 180,
    y: (rotation.y * Math.PI) / 180,
    z: (rotation.z * Math.PI) / 180,
  };

  let mirroredRotation = { ...rotationRad };

  switch (plane) {
    case 'xy': // Mirror across X-Y plane (flip Z)
      mirroredRotation.x = -rotationRad.x;
      mirroredRotation.y = -rotationRad.y;
      break;
    case 'xz': // Mirror across X-Z plane (flip Y)
      mirroredRotation.x = -rotationRad.x;
      mirroredRotation.z = -rotationRad.z;
      break;
    case 'yz': // Mirror across Y-Z plane (flip X)
      mirroredRotation.y = -rotationRad.y;
      mirroredRotation.z = -rotationRad.z;
      break;
  }

  // Convert back to degrees
  return {
    x: (mirroredRotation.x * 180) / Math.PI,
    y: (mirroredRotation.y * 180) / Math.PI,
    z: (mirroredRotation.z * 180) / Math.PI,
  };
}

function sanitizeVector3(v, defaultValue = { x: 0, y: 0, z: 0 }) {
  if (!v) {
    return new THREE.Vector3(defaultValue.x, defaultValue.y, defaultValue.z);
  }
  return new THREE.Vector3(v.x ?? defaultValue.x, v.y ?? defaultValue.y, v.z ?? defaultValue.z);
}

function updatePolarArrayVector3(v, properties, arrayIndex) {
  const polarCount = properties.polarCount || 6;
  const polarFullOverride = properties.polarFullOverride || false;
  const polarAngle = properties.polarAngle || 90;
  const polarAxis = properties.polarAxis || 'y';

  // Full 360: evenly space all items around the circle
  // Partial angle: spread items from 0° to polarAngle (divide by gaps, not count)
  const angleStep = polarFullOverride ? 360 / polarCount : polarCount > 1 ? polarAngle / (polarCount - 1) : 0;
  const angle = (angleStep * arrayIndex * Math.PI) / 180;

  const vector3 = new THREE.Vector3(v.x, v.y, v.z);

  const matrix = new THREE.Matrix4();
  switch (polarAxis) {
    case 'x':
      matrix.makeRotationX(angle);
      break;
    case 'y':
      matrix.makeRotationY(angle);
      break;
    case 'z':
      matrix.makeRotationZ(angle);
      break;
  }

  const vector3out = vector3.applyMatrix4(matrix);
  return { x: vector3out.x, y: vector3out.y, z: vector3out.z };
}

function updatePolarArrayRotation(rotation, properties, arrayIndex) {
  const polarCount = properties.polarCount || 6;
  const polarFullOverride = properties.polarFullOverride || false;
  const polarAngle = properties.polarAngle || 90;
  const polarAxis = properties.polarAxis || 'y';

  const angleStep = polarFullOverride ? 360 / polarCount : polarCount > 1 ? polarAngle / (polarCount - 1) : 0;
  const angleDeg = angleStep * arrayIndex;

  const result = { ...rotation };
  switch (polarAxis) {
    case 'x':
      result.x = (result.x || 0) + angleDeg;
      break;
    case 'y':
      result.y = (result.y || 0) + angleDeg;
      break;
    case 'z':
      result.z = (result.z || 0) + angleDeg;
      break;
  }

  return result;
}

function updateLinearArrayVector3(v, properties, arrayIndex) {
  const vector3 = new THREE.Vector3(v.x, v.y, v.z);
  const arrayTranslation = sanitizeVector3(properties.arrayTranslation, {
    x: 2,
    y: 0,
    z: 0,
  });
  const arrayRotation = sanitizeVector3(properties.arrayRotation);
  const translationVector = new THREE.Vector3(
    arrayTranslation.x * arrayIndex || 0,
    arrayTranslation.y * arrayIndex || 0,
    arrayTranslation.z * arrayIndex || 0
  );

  const rotationEuler = new THREE.Euler(
    (arrayRotation.x * arrayIndex * Math.PI) / 180 || 0,
    (arrayRotation.y * arrayIndex * Math.PI) / 180 || 0,
    (arrayRotation.z * arrayIndex * Math.PI) / 180 || 0
  );

  const matrix = new THREE.Matrix4();
  matrix.makeRotationFromEuler(rotationEuler);

  const vector3out = vector3.applyMatrix4(matrix).add(translationVector);

  return { x: vector3out.x, y: vector3out.y, z: vector3out.z };
}

export class Group extends BaseObject {
  constructor(createProperties) {
    super();
    const group = new THREE.Group();
    // Initialize group properties
    group.userData.groupMode = 'normal';
    group.userData.symmetryPlane = 'yz';
    group.userData.symmetryOffset = 0;
    group.userData.arrayCount = 3;

    group.userData.arrayTranslation = { x: 2, y: 0, z: 0 };
    group.userData.arrayRotation = { x: 0, y: 0, z: 0 };

    group.userData.polarCount = 6;
    group.userData.polarAngle = 90;
    group.userData.polarFullOverride = false;
    group.userData.polarAxis = 'y';

    // Target translation properties for projectors
    group.userData.targetTranslation = { x: 0, y: 0, z: 0 };
    group.userData.arrayTargetTranslation = { x: 0, y: 0, z: 0 };

    // Store reference to original children for transformations
    group.userData.originalChildren = [];
    group.userData.generatedChildren = [];

    group.clearSelection = () => {
      group.userData.selected = false;
    };

    group.select = () => {
      group.userData.selected = true;
    };

    // Override add method to track original children
    const originalAdd = group.add.bind(group);
    group.add = (...objects) => {
      // Ensure arrays are initialized
      if (!group.userData.originalChildren) group.userData.originalChildren = [];
      if (!group.userData.generatedChildren) group.userData.generatedChildren = [];

      objects.forEach((obj) => {
        if (!group.userData.generatedChildren.includes(obj)) {
          group.userData.originalChildren.push(obj);
        }
      });
      return originalAdd(...objects);
    };

    // Override remove method to track original children
    const originalRemove = group.remove.bind(group);
    group.remove = (...objects) => {
      // Ensure arrays are initialized
      if (!group.userData.originalChildren) group.userData.originalChildren = [];
      if (!group.userData.generatedChildren) group.userData.generatedChildren = [];

      objects.forEach((obj) => {
        const originalIndex = group.userData.originalChildren.indexOf(obj);
        if (originalIndex !== -1) {
          group.userData.originalChildren.splice(originalIndex, 1);
        }
        const generatedIndex = group.userData.generatedChildren.indexOf(obj);
        if (generatedIndex !== -1) {
          group.userData.generatedChildren.splice(generatedIndex, 1);
        }
      });
      return originalRemove(...objects);
    };

    // Method to apply properties to the group
    group.applyProperties = (properties) => {
      applyProperties(group, properties, false);

      if ('groupMode' in properties) {
        group.userData.groupMode = properties.groupMode;
      }

      if ('symmetryPlane' in properties) {
        group.userData.symmetryPlane = properties.symmetryPlane;
      }

      if ('symmetryOffset' in properties) {
        group.userData.symmetryOffset = properties.symmetryOffset;
      }

      if ('arrayCount' in properties) {
        group.userData.arrayCount = properties.arrayCount;
      }

      if ('arrayTranslation' in properties) {
        if (typeof properties.arrayTranslation === 'object') {
          group.userData.arrayTranslation = { ...properties.arrayTranslation };
        }
      }

      if ('arrayRotation' in properties) {
        if (typeof properties.arrayRotation === 'object') {
          group.userData.arrayRotation = { ...properties.arrayRotation };
        }
      }

      // Handle individual array translation components
      if ('arrayTranslation.x' in properties) {
        if (!group.userData.arrayTranslation) group.userData.arrayTranslation = { x: 0, y: 0, z: 0 };
        group.userData.arrayTranslation.x = properties['arrayTranslation.x'];
      }

      if ('arrayTranslation.y' in properties) {
        if (!group.userData.arrayTranslation) group.userData.arrayTranslation = { x: 0, y: 0, z: 0 };
        group.userData.arrayTranslation.y = properties['arrayTranslation.y'];
      }

      if ('arrayTranslation.z' in properties) {
        if (!group.userData.arrayTranslation) group.userData.arrayTranslation = { x: 0, y: 0, z: 0 };
        group.userData.arrayTranslation.z = properties['arrayTranslation.z'];
      }

      if ('polarCount' in properties) {
        group.userData.polarCount = properties.polarCount;
      }

      if ('polarAngle' in properties) {
        group.userData.polarAngle = properties.polarAngle;
      }

      if ('polarFullOverride' in properties) {
        group.userData.polarFullOverride = properties.polarFullOverride;
      }

      if ('polarAxis' in properties) {
        group.userData.polarAxis = properties.polarAxis;
      }

      // Handle individual array rotation components
      if ('arrayRotation.x' in properties) {
        if (!group.userData.arrayRotation) group.userData.arrayRotation = { x: 0, y: 0, z: 0 };
        group.userData.arrayRotation.x = properties['arrayRotation.x'];
      }

      if ('arrayRotation.y' in properties) {
        if (!group.userData.arrayRotation) group.userData.arrayRotation = { x: 0, y: 0, z: 0 };
        group.userData.arrayRotation.y = properties['arrayRotation.y'];
      }

      if ('arrayRotation.z' in properties) {
        if (!group.userData.arrayRotation) group.userData.arrayRotation = { x: 0, y: 0, z: 0 };
        group.userData.arrayRotation.z = properties['arrayRotation.z'];
      }

      // Handle target translation properties for symmetry
      if ('targetTranslation' in properties) {
        if (typeof properties.targetTranslation === 'object') {
          group.userData.targetTranslation = { ...properties.targetTranslation };
        }
      }

      if ('targetTranslation.x' in properties) {
        if (!group.userData.targetTranslation) group.userData.targetTranslation = { x: 0, y: 0, z: 0 };
        group.userData.targetTranslation.x = properties['targetTranslation.x'];
      }

      if ('targetTranslation.y' in properties) {
        if (!group.userData.targetTranslation) group.userData.targetTranslation = { x: 0, y: 0, z: 0 };
        group.userData.targetTranslation.y = properties['targetTranslation.y'];
      }

      if ('targetTranslation.z' in properties) {
        if (!group.userData.targetTranslation) group.userData.targetTranslation = { x: 0, y: 0, z: 0 };
        group.userData.targetTranslation.z = properties['targetTranslation.z'];
      }

      // Handle array target translation properties
      if ('arrayTargetTranslation' in properties) {
        if (typeof properties.arrayTargetTranslation === 'object') {
          group.userData.arrayTargetTranslation = {
            ...properties.arrayTargetTranslation,
          };
        }
      }

      if ('arrayTargetTranslation.x' in properties) {
        if (!group.userData.arrayTargetTranslation) group.userData.arrayTargetTranslation = { x: 0, y: 0, z: 0 };
        group.userData.arrayTargetTranslation.x = properties['arrayTargetTranslation.x'];
      }

      if ('arrayTargetTranslation.y' in properties) {
        if (!group.userData.arrayTargetTranslation) group.userData.arrayTargetTranslation = { x: 0, y: 0, z: 0 };
        group.userData.arrayTargetTranslation.y = properties['arrayTargetTranslation.y'];
      }

      if ('arrayTargetTranslation.z' in properties) {
        if (!group.userData.arrayTargetTranslation) group.userData.arrayTargetTranslation = { x: 0, y: 0, z: 0 };
        group.userData.arrayTargetTranslation.z = properties['arrayTargetTranslation.z'];
      }
    };

    // Refresh clones based on the current group mode
    group.refreshClones = ({ getChildren, safeClone, addObject, removeAllClones }) => {
      removeAllClones();

      const groupCode = group.userData.code;
      const groupData = group.userData;
      const children = getChildren(groupCode);

      if (groupData.groupMode === 'symmetry') {
        children.forEach((child) => {
          const cloneData = safeClone(child);
          const position = child.data.position || { x: 0, y: 0, z: 0 };

          cloneData.data.position = updateSymmetryVector3(position, groupData);

          if (child.data.target) {
            cloneData.data.target = updateSymmetryVector3(child.data.target, groupData);
          }
          if (child.data.rotation) {
            cloneData.data.rotation = updateSymmetryRotation(child.data.rotation, groupData);
          }

          cloneData.code = cloneData.code + '_clone';
          cloneData.clone = true;
          cloneData.data.clonedCode = child.code;
          addObject(cloneData.type, groupCode, cloneData);
        });
      } else if (groupData.groupMode === 'linear_array') {
        const arrayCount = groupData.arrayCount || 3;

        for (let i = 1; i < arrayCount; i++) {
          children.forEach((child) => {
            const cloneData = safeClone(child);
            const position = cloneData.data.position || { x: 0, y: 0, z: 0 };

            cloneData.data.position = updateLinearArrayVector3(position, groupData, i);
            if (cloneData.data.target) {
              cloneData.data.target = updateLinearArrayVector3(cloneData.data.target, groupData, i);
            }
            cloneData.code = cloneData.code + '_clone_' + i;
            cloneData.clone = true;
            cloneData.data.clonedCode = child.code;
            addObject(cloneData.type, groupCode, cloneData);
          });
        }
      } else if (groupData.groupMode === 'polar_array') {
        const polarCount = groupData.polarCount || 6;

        for (let i = 1; i < polarCount; i++) {
          children.forEach((child) => {
            const cloneData = safeClone(child);
            const position = cloneData.data.position || { x: 0, y: 0, z: 0 };

            cloneData.data.position = updatePolarArrayVector3(position, groupData, i);
            if (cloneData.data.target) {
              cloneData.data.target = updatePolarArrayVector3(cloneData.data.target, groupData, i);
            }
            if (cloneData.data.rotation) {
              cloneData.data.rotation = updatePolarArrayRotation(cloneData.data.rotation, groupData, i);
            }
            cloneData.code = cloneData.code + '_clone_' + i;
            cloneData.clone = true;
            cloneData.data.clonedCode = child.code;
            addObject(cloneData.type, groupCode, cloneData);
          });
        }
      }
    };

    return group;
  }
}
